import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faCreditCard,
  faRightFromBracket,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import WixUninstallModal from './WixUninstallModal.js';
import './UserMenu.css';

// Floating account menu, pinned to the top-right of every authenticated
// page. Replaces the old sidebar "Manage" / "Logout" admin rows.
// Opened from the Wix dashboard (`wixSite`), it offers "Uninstall app" where
// others see "Logout".
const UserMenuContent = ({ onLogout, showLogout = true, wixSite = null, botInContext = false, selectedBotId = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { requestUnsavedAction } = useUnsavedChanges();
  // Billing target: an individual agent's page only when a bot is actually in
  // context (the header shows the bot selector — i.e. NOT the workspace level).
  // `selectedBotId` alone isn't enough: it persists across workspace pages.
  const billingPath = botInContext && selectedBotId ? `/billing/${selectedBotId}` : "/billing";
  const [open, setOpen] = useState(false);
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const go = (path) => {
    setOpen(false);
    requestUnsavedAction(() => navigate(path));
  };

  const handleLogout = () => {
    setOpen(false);
    requestUnsavedAction(() => onLogout?.());
  };

  const firstName = localStorage.getItem('first_name') || '';
  const initial = firstName.trim().charAt(0).toUpperCase() || 'U';

  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className={`user-menu-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        title={firstName || t('menu_account')}
      >
        <svg
          className="user-menu-avatar"
          viewBox="0 0 32 32"
          aria-hidden="true"
          focusable="false"
        >
          <text x="16" y="16" textAnchor="middle" dominantBaseline="central">
            {initial}
          </text>
        </svg>
      </button>

      {open && (
        <div className="user-menu-dropdown" role="menu">
          {firstName && (
            <div className="user-menu-greeting">{firstName}</div>
          )}

          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => go('/my-account')}
          >
            <span className="user-menu-item-icon">
              <FontAwesomeIcon icon={faGear} />
            </span>
            {t('user_settings')}
          </button>

          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => go(billingPath)}
          >
            <span className="user-menu-item-icon">
              <FontAwesomeIcon icon={faCreditCard} />
            </span>
            {t('menu_billing')}
          </button>

          {showLogout && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-item logout"
                role="menuitem"
                onClick={handleLogout}
              >
                <span className="user-menu-item-icon">
                  <FontAwesomeIcon icon={faRightFromBracket} />
                </span>
                {t('logout')}
              </button>
            </>
          )}

          {!showLogout && wixSite && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-item logout"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  setUninstallOpen(true);
                }}
              >
                <span className="user-menu-item-icon">
                  <FontAwesomeIcon icon={faTrashCan} />
                </span>
                {t('wix_uninstall_menu')}
              </button>
            </>
          )}
        </div>
      )}

      {wixSite && (
        <WixUninstallModal
          open={uninstallOpen}
          onClose={() => setUninstallOpen(false)}
          wixSite={wixSite}
        />
      )}
    </div>
  );
};

// Account security pages must not mount the agent provider just for the menu.
export const AccountUserMenu = (props) => <UserMenuContent {...props} />;
const UserMenu = (props) => {
  const { selectedBotId } = useSelectedBot();
  return <UserMenuContent {...props} selectedBotId={selectedBotId} />;
};
export default UserMenu;
